import { cn } from '../lib/utils';
import { Sparkline } from './Sparkline';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';

/**
 * Tarjeta de KPI.
 *
 * Decisiones:
 * · El número manda: tipografía grande, tabular (no "baila" al actualizarse).
 * · El acento es semántico, no decorativo: verde = mejor, rojo = peor,
 *   ámbar = ojo. Como raíl de 3px y tinte suave, nunca un bloque saturado.
 * · Mientras llegan los datos se muestra un skeleton con la forma real de la
 *   tarjeta (no un spinner, y nunca un "0" falso).
 */
const TONOS = {
  primary:     { rail: 'bg-primary',     icon: 'text-primary',     chip: 'bg-primary/[0.08] text-primary',     stroke: 'hsl(var(--primary))' },
  success:     { rail: 'bg-success',     icon: 'text-success',     chip: 'bg-success/10 text-success',         stroke: 'hsl(var(--success))' },
  warning:     { rail: 'bg-warning',     icon: 'text-warning',     chip: 'bg-warning/15 text-warning',         stroke: 'hsl(var(--warning))' },
  destructive: { rail: 'bg-destructive', icon: 'text-destructive', chip: 'bg-destructive/10 text-destructive', stroke: 'hsl(var(--destructive))' },
  info:        { rail: 'bg-info',        icon: 'text-info',        chip: 'bg-info/10 text-info',               stroke: 'hsl(var(--info))' },
};

const Flecha = ({ tendencia }) => {
  if (tendencia === 'up') return <ArrowUpRight className="h-3.5 w-3.5" />;
  if (tendencia === 'down') return <ArrowDownRight className="h-3.5 w-3.5" />;
  return <Minus className="h-3.5 w-3.5" />;
};

export const KPICard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  tendencia,         // 'up' | 'down' | 'flat'
  tendenciaTexto,    // "12% vs mes pasado"
  serie,             // [n, n, n...] para el sparkline
  tono = 'primary',
  cargando = false,
  onClick,
  className,
}) => {
  const t = TONOS[tono] || TONOS.primary;

  if (cargando) {
    return (
      <div className={cn('relative overflow-hidden rounded-xl border border-border/70 bg-card p-4', className)}>
        <div className="skeleton h-3 w-24" />
        <div className="skeleton mt-4 h-7 w-20" />
        <div className="skeleton mt-3 h-2.5 w-28" />
      </div>
    );
  }

  const Comp = onClick ? 'button' : 'div';

  return (
    <Comp
      onClick={onClick}
      type={onClick ? 'button' : undefined}
      className={cn(
        'group relative flex h-full flex-col overflow-hidden rounded-xl border border-border/70 bg-card p-4 text-left transition-all duration-200',
        'shadow-sm hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-md',
        onClick && 'cursor-pointer',
        className,
      )}
      data-testid={`kpi-${title?.toLowerCase().replace(/\s/g, '-')}`}
    >
      <span className={cn('absolute left-0 top-0 h-full w-[3px]', t.rail)} aria-hidden="true" />

      <div className="flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{title}</p>
          {Icon && (
            <span className={cn('grid h-7 w-7 shrink-0 place-items-center rounded-lg', t.chip)}>
              <Icon className={cn('h-3.5 w-3.5', t.icon)} />
            </span>
          )}
        </div>

        <p className="mt-2.5 font-display text-[26px] font-extrabold leading-none tracking-tight tnum">{value}</p>

        <div className="mt-1.5 min-h-[16px]">
          {tendencia ? (
            <span
              className={cn(
                'inline-flex items-center gap-1 text-[12px] font-semibold',
                tendencia === 'up' ? 'text-success' : tendencia === 'down' ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              <Flecha tendencia={tendencia} />
              {tendenciaTexto}
            </span>
          ) : subtitle ? (
            <p className="text-[11.5px] leading-tight text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>

        {serie?.length > 1 && (
          <div className="mt-auto -mb-1 w-full pt-3 opacity-80 transition-opacity group-hover:opacity-100">
            <Sparkline data={serie} stroke={t.stroke} height={30} className="w-full" />
          </div>
        )}
      </div>
    </Comp>
  );
};
